import { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, Stack, Chip, Button, useTheme, alpha } from "@mui/material";
import { TableChart } from "@mui/icons-material";
import { useResponsive } from "../../../core/hooks/useResponsive";
import BTTradeCard from "./BTTradeCard";

const CARD_PAGE_SIZE = 8;

const SORT_OPTIONS = [
  { key: "plDesc", label: "P/L high→low" },
  { key: "plAsc", label: "P/L low→high" },
  { key: "date", label: "Newest first" },
];

const fmt = (n) => (n != null && n !== "" && !Number.isNaN(Number(n)) ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }) : n);

const columns = [
  { field: "buyDate", headerName: "Buy date", flex: 1, minWidth: 110 },
  {
    field: "buyPrice",
    headerName: "Buy",
    flex: 0.8,
    minWidth: 88,
    type: "number",
    valueFormatter: (value) => fmt(value),
  },
  { field: "buyQty", headerName: "Qty", flex: 0.5, minWidth: 64, type: "number" },
  {
    field: "saleDate",
    headerName: "Sale date",
    flex: 1,
    minWidth: 110,
    // Still open at the end of the window: valued at the last close (backtest tradeSimulator isOpen).
    valueFormatter: (value, row) => (row?.isOpen ? `${value} (open)` : value),
  },
  {
    field: "salePrice",
    headerName: "Sale",
    flex: 0.8,
    minWidth: 88,
    type: "number",
    valueFormatter: (value) => fmt(value),
  },
  {
    field: "margin",
    headerName: "P/L",
    flex: 0.9,
    minWidth: 96,
    type: "number",
    valueFormatter: (value) => fmt(value),
    cellClassName: (params) => {
      const v = Number(params.value);
      if (Number.isNaN(v)) return "";
      if (v > 0) return "bt-pl-profit";
      if (v < 0) return "bt-pl-loss";
      return "bt-pl-neutral";
    },
  },
  { field: "holdDays", headerName: "Days", flex: 0.5, minWidth: 72, type: "number" },
];

const rowKey = (row) => `${row.token ?? "r"}-${row.buyDate}-${row.buyPrice}-${row.saleDate}`;

const BTHistory = ({ tradeHistory }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { isCompact } = useResponsive();
  const [visibleCount, setVisibleCount] = useState(CARD_PAGE_SIZE);
  const [sortKey, setSortKey] = useState("date");

  const rows = useMemo(() => tradeHistory || [], [tradeHistory]);

  // Card mode has no column headers to sort by, so the grid's sorting is offered as
  // chips rather than quietly lost.
  const sortedRows = useMemo(() => {
    const out = [...rows];
    if (sortKey === "plDesc") out.sort((a, b) => (Number(b.margin) || 0) - (Number(a.margin) || 0));
    else if (sortKey === "plAsc") out.sort((a, b) => (Number(a.margin) || 0) - (Number(b.margin) || 0));
    else out.sort((a, b) => String(b.buyDate || "").localeCompare(String(a.buyDate || "")));
    return out;
  }, [rows, sortKey]);

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No trade rows for this run.</Typography>
      </Box>
    );
  }

  const header = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: isCompact ? 1 : 2 }}>
      <TableChart sx={{ color: primary }} />
      <Typography variant="subtitle1" fontWeight={700}>
        {rows.length} trade{rows.length !== 1 ? "s" : ""}
      </Typography>
    </Box>
  );

  if (isCompact) {
    /* --- Card list (<768px): the grid needs 628px of columns, a phone has ~344px --- */
    return (
      <Box>
        {header}
        {rows.length > 1 && (
          <Stack direction="row" spacing={0.75} sx={{ mb: 1, overflowX: "auto", pb: 0.25 }}>
            {SORT_OPTIONS.map((opt) => (
              <Chip
                key={opt.key}
                label={opt.label}
                size="small"
                onClick={() => setSortKey(opt.key)}
                variant={sortKey === opt.key ? "filled" : "outlined"}
                color={sortKey === opt.key ? "primary" : "default"}
                sx={{ fontWeight: 700, fontSize: "0.7rem", flexShrink: 0 }}
              />
            ))}
          </Stack>
        )}
        <Stack spacing={1}>
          {sortedRows.slice(0, visibleCount).map((row) => (
            <BTTradeCard key={rowKey(row)} row={row} />
          ))}
        </Stack>
        {visibleCount < sortedRows.length && (
          <Button
            fullWidth
            size="small"
            onClick={() => setVisibleCount((c) => c + CARD_PAGE_SIZE)}
            sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
          >
            Show {Math.min(CARD_PAGE_SIZE, sortedRows.length - visibleCount)} more
            {` (${visibleCount} of ${sortedRows.length})`}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {header}
      <Box
        sx={{
          width: "100%",
          borderRadius: 2,
          border: `1px solid ${alpha(primary, 0.12)}`,
          overflow: "hidden",
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: alpha(primary, 0.06),
            borderBottom: `2px solid ${alpha(primary, 0.15)}`,
          },
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.5 },
          "& .MuiDataGrid-row:nth-of-type(even)": { bgcolor: alpha(theme.palette.action.hover, 0.04) },
          "& .MuiDataGrid-row:hover": { bgcolor: alpha(primary, 0.06) },
          "& .bt-pl-profit": { color: theme.palette.profitColor ?? "#16a34a", fontWeight: 700 },
          "& .bt-pl-loss": { color: theme.palette.lossColor ?? "#dc2626", fontWeight: 700 },
          "& .bt-pl-neutral": { color: "text.secondary" },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          getRowId={rowKey}
          rowHeight={44}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};

export default BTHistory;
